/**
 * Author: Ben Quackenbush
 * Date: 12/29/2025
 * Description: Provides a way to fetch information from Shopify GraphQL.
 *    More GraphQL queries can be added to this file in the future.
 *
 * ToDos: Need to update the API version periodically.
 */
const SHOPIFY_STOREFRONT_ACCESS_TOKEN = '9e9aa1612ecd375fde40752fc5b5b258';
export const fetchGraphQL = async (graphQLQueryValue, variables) => {
  try {
    const query = { query: graphQLQueryValue };
    if (variables) {
      query.variables = variables;
    }
    const fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify(query),
    };
    const response = fetch('/api/2025-10/graphql.json', fetchOptions);
    const result = await response;
    if (result.status >= 300) {
      const body = await result.text();
      console.error('Error returned, fetchGraphQL', body);
      return null;
    }
    return result.json();
  } catch (e) {
    console.error('Error during fetchGraphQL', e);
    return null;
  }
};
export const productsInfoQuery = `
query ProductIds($productIds: [ID!]!) {
  nodes(ids: $productIds) {
    ... on Product {
      id
      title
      options {
        id
        name
        optionValues {
          id
          name
          swatch {
            color
            image {
              ... on MediaImage {
                image {
                  url(transform: {maxWidth: 28})
                }
              }
            }
          }
        }
      }
      variants(first: 250) {
        nodes {
          id
          selectedOptions {
            name
            value
            
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          currentlyNotInStock
          availableForSale
          stopSelling: metafield(namespace: "custom", key: "stop_selling") {
            value
          }
          image {
            url(transform: {maxWidth: 254})
            altText
          }
        }
      }
    }
  }
}
`;

// Shopify handles are lowercase alphanumeric + hyphens; strip anything else so
// the value is safe to interpolate into the aliased GraphQL query below.
const sanitizeHandle = (handle) =>
  String(handle || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '');

/**
 * Builds a batched, aliased Storefront query that looks up multiple collections
 * by handle in a single request. Collections expose a native `image`.
 * @param {string[]} handles
 * @returns {string}
 */
export const buildCollectionsInfoQuery = (handles) => {
  const fields = handles
    .map(
      (handle, index) =>
        `c${index}: collection(handle: "${sanitizeHandle(handle)}") { ...CollectionCardFields }`,
    )
    .join('\n');
  return `
    query CollectionsInfo {
      ${fields}
    }
    fragment CollectionCardFields on Collection {
      handle
      title
      description
      onlineStoreUrl
      image {
        url(transform: { maxWidth: 400 })
        altText
      }
    }
  `;
};

/**
 * Builds a batched, aliased Storefront query that looks up multiple pages by
 * handle in a single request. Pages have no native image, so the card image is
 * pulled from a `custom.featured_image` file-reference metafield when present.
 * @param {string[]} handles
 * @returns {string}
 */
export const buildPagesInfoQuery = (handles) => {
  const fields = handles
    .map(
      (handle, index) =>
        `p${index}: page(handle: "${sanitizeHandle(handle)}") { ...PageCardFields }`,
    )
    .join('\n');
  return `
    query PagesInfo {
      ${fields}
    }
    fragment PageCardFields on Page {
      handle
      title
      bodySummary
      onlineStoreUrl
      featuredImage: metafield(namespace: "custom", key: "featured_image") {
        reference {
          ... on MediaImage {
            image {
              url(transform: { maxWidth: 400 })
              altText
            }
          }
        }
      }
    }
  `;
};

/**
 * Builds a batched, aliased Storefront query that looks up multiple blog
 * articles. Klevu CMS records often point at `/blogs/<blog>/<article>` URLs,
 * which are not retrievable via `page(handle: ...)`.
 * @param {{ blogHandle: string, articleHandle: string }[]} articles
 * @returns {string}
 */
export const buildArticlesInfoQuery = (articles) => {
  const fields = articles
    .map((article, index) => {
      const blogHandle = sanitizeHandle(article.blogHandle);
      const articleHandle = sanitizeHandle(article.articleHandle);
      return `a${index}: blog(handle: "${blogHandle}") { articleByHandle(handle: "${articleHandle}") { ...ArticleCardFields } }`;
    })
    .join('\n');
  return `
    query ArticlesInfo {
      ${fields}
    }
    fragment ArticleCardFields on Article {
      handle
      title
      excerpt
      onlineStoreUrl
      image {
        url(transform: { maxWidth: 400 })
        altText
      }
    }
  `;
};
